import { apiCall, isBackendConfigured } from './api-call'
import {
  QuickPassPing,
  RespondToPingResultPayload,
} from '../_types/quickpass-ping'

const safeParseArray = <Result>(raw: string): Result[] => {
  try {
    const parsed = JSON.parse(raw) as Result[]
    return Array.isArray(parsed) === true ? parsed : []
  } catch (error) {
    console.error('[quickpass-pings] parse failed', error, raw)
    return []
  }
}

const safeParseObject = <Result>(raw: string): Result | null => {
  try {
    return JSON.parse(raw) as Result
  } catch (error) {
    console.error('[quickpass-pings] parse failed', error, raw)
    return null
  }
}

/**
 * Loads the renter's pings from the backend. Returns an empty array
 * when the backend isn't configured (dev-bypass mode) so the UI can
 * gracefully render an empty state.
 *
 * @param {string} quickPassUserId - the renter
 * @returns {Promise<QuickPassPing[]>} the ping list, newest first
 */
const fetchQuickPassPings = async (
  quickPassUserId: string,
): Promise<QuickPassPing[]> => {
  if (isBackendConfigured() === false) {
    return []
  }

  const response = await apiCall({
    group: 'quickpass',
    command: 'getpings',
    data: { quickPassUserId },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  return safeParseArray<QuickPassPing>(response.data)
}

/**
 * Stamps a ping as viewed. No-ops when the backend isn't configured.
 *
 * @param {string} quickPassUserId - the renter
 * @param {string} pingId - the ping ID
 * @returns {Promise<void>}
 */
const markPingViewedRemote = async (
  quickPassUserId: string,
  pingId: string,
): Promise<void> => {
  if (isBackendConfigured() === false) {
    return
  }

  const response = await apiCall({
    group: 'quickpass',
    command: 'markpingviewed',
    data: { quickPassUserId, pingId },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }
}

/**
 * Sends the renter's response to a ping. When 'interested', the
 * backend creates a lead doc visible to the sending client.
 *
 * @param {string} quickPassUserId - the renter
 * @param {string} pingId - the ping ID
 * @param {'interested' | 'not-interested'} response - the renter's choice
 * @returns {Promise<RespondToPingResultPayload>} the result payload
 */
const respondToPingRemote = async (
  quickPassUserId: string,
  pingId: string,
  response: 'interested' | 'not-interested',
): Promise<RespondToPingResultPayload> => {
  if (isBackendConfigured() === false) {
    return { leadId: null }
  }

  const apiResponse = await apiCall({
    group: 'quickpass',
    command: 'respondtoping',
    data: { quickPassUserId, pingId, response },
  })

  if (apiResponse.code !== 200) {
    throw new Error(apiResponse.message)
  }

  return (
    safeParseObject<RespondToPingResultPayload>(apiResponse.data) ?? {
      leadId: null,
    }
  )
}

export { fetchQuickPassPings, markPingViewedRemote, respondToPingRemote }
